// src/components/admin/FlowToolbar.jsx
import React, { useRef } from 'react';
import {
  WORKFLOW_ADDABLE_NODE_TYPES,
  WORKFLOW_NODE_DRAG_TYPE,
} from '../../utils/workflowNodeTypes';
import WorkflowLifecycleBadge from '../../../shared/components/WorkflowLifecycleBadge';

// What this version is, on the badge that names its state. The one-off bar announcing a fork is
// gone after a reload and the editor can be reached straight from the workflow list, so the
// standing answer to "which version am I looking at" has to live here — but as a version number
// and a hover, not a sentence in the toolbar. `v5` plus `Draft` is the whole answer at a glance;
// which version is live is what you hover to find out.
function lifecycleTitle({ workflowState, replaces, pendingDraft }) {
  if (workflowState === 'published') {
    if (!pendingDraft) return 'Live. Editing creates a new draft; this version keeps running.';

    const draftVersion = versionLabel(pendingDraft);
    return draftVersion
      ? `Live. Editing joins the existing draft ${draftVersion}.`
      : 'Live. Editing joins the draft that already exists.';
  }

  if (workflowState === 'draft') {
    if (!replaces) return 'Not published yet. There is no earlier version of this workflow.';

    const liveVersion = versionLabel(replaces);
    return liveVersion
      ? `Published version ${liveVersion} stays live until you publish this draft.`
      : 'The published version stays live until you publish this draft.';
  }

  if (workflowState === 'deprecated') return 'Superseded by a newer published version.';
  return null;
}

// `v5`, or nothing at all when the API has not said. Version is derived server-side by walking
// the `replaces_id` chain, so an older payload simply omits it and the label disappears rather
// than rendering `vundefined`.
function versionLabel(workflow) {
  const version = workflow?.version;
  return Number.isFinite(version) && version > 0 ? `v${version}` : '';
}

export default function FlowToolbar({
  onNewWorkflow,
  onOpenWorkflow,
  onSaveWorkflow,
  onDownloadJSON,
  onUploadJSON,
  onAutoLayout,
  autoLayoutDisabled = true,
  onPreview,
  onPublish,
  onEditWorkflow,
  saveStatus,
  workflowName,
  workflowState,
  setNodes,
  setEdges,
  showDetailedContent,
  onToggleDetailedContent,
  warningItems = [],
  onValidationIssueClick,
  publishDisabled = false,
  selectedAddNodeType,
  onSelectAddNodeType,
  onDiscardDraft,
  workflowVersion = null,
  replaces = null,
  pendingDraft = null,
}) {
  const fileInputRef = useRef(null);

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      try {
        const jsonData = JSON.parse(text);
        onUploadJSON(jsonData);
      } catch {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  }

  function handleNodeDragStart(event, nodeType) {
    event.dataTransfer.setData(WORKFLOW_NODE_DRAG_TYPE, nodeType);
    event.dataTransfer.effectAllowed = 'move';
  }

  const stateTitle = lifecycleTitle({ workflowState, replaces, pendingDraft });
  const version = versionLabel({ version: workflowVersion });
  const warningsTooltip = warningItems.map((w) => w.message).join('\n');
  const hasClickableIssues = warningItems.some((w) => w.violation && onValidationIssueClick);

  return (
    <div className="bg-light d-flex align-items-center px-3 py-2 border-bottom">
      {/* Which workflow this is. The editor used to name it nowhere at all — not here, not on
          the page, not in the title bar — which matters most right after editing a published
          workflow forks a new draft and swaps the id in the URL. Truncated rather than cut to a
          character count, so the full name is one hover away and the toolbar never reflows. */}
      {workflowName && (
        <>
          <span
            className="fw-semibold text-truncate"
            style={{ maxWidth: '18rem' }}
            title={workflowName}
          >
            {workflowName}
          </span>
          {version && (
            <span className="text-muted small ms-2 text-nowrap" title="Version of this workflow">
              {version}
            </span>
          )}
          {workflowState && (
            <WorkflowLifecycleBadge
              variant={workflowState}
              className="ms-2"
              title={stateTitle}
            />
          )}
          <div className="vr mx-3" />
        </>
      )}

      <div className="text-tiny mb-2 mx-2">Workflow</div>
      <div className="btn-group me-3" role="group" aria-label="Workflow">
        <button
          className="btn btn-sm btn-white"
          onClick={() => onEditWorkflow()}
          title="Workflow settings — name, description, scope and crew"
          aria-label="Workflow settings"
        >
          <i className="bi bi-gear" />
        </button>
        <button
          className="btn btn-sm btn-white"
          onClick={() => onNewWorkflow(setNodes, setEdges)}
          title="New workflow — clears the canvas and starts a fresh one"
          aria-label="New workflow"
        >
          <i className="bi bi-file-earmark-plus" />
        </button>
        <button
          className="btn btn-sm btn-white"
          onClick={() => onOpenWorkflow()}
          title="Open another workflow"
          aria-label="Open another workflow"
        >
          <i className="bi bi-folder" />
        </button>
        <button
          className="btn btn-sm btn-white"
          onClick={() => onSaveWorkflow()}
          title="Save this workflow"
          aria-label="Save workflow"
        >
          <i className="bi bi-floppy" />
        </button>
      </div>
      <div className="text-tiny mb-2 mx-2">JSON</div>
      <div className="btn-group me-3" role="group" aria-label="JSON">
        <button
          className="btn btn-sm btn-white"
          onClick={() => onDownloadJSON()}
          title="Download this workflow as JSON"
          aria-label="Download workflow JSON"
        >
          <i className="bi bi-cloud-download"></i>
        </button>
        <input
          type="file"
          accept=".json"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          className="btn btn-sm btn-white"
          onClick={handleUploadClick}
          title="Replace the canvas with a workflow from a JSON file"
          aria-label="Upload workflow JSON"
        >
          <i className="bi bi-cloud-upload"></i>
        </button>
      </div>

      <div className="text-tiny mb-2 mx-2">Node</div>
      <div className="d-flex align-items-center me-auto">
        <div className="btn-group me-2" role="group" aria-label="Add node type">
          {WORKFLOW_ADDABLE_NODE_TYPES.map((nodeType) => {
            const isSelected = selectedAddNodeType === nodeType.type;
            return (
              <button
                key={nodeType.type}
                type="button"
                className="btn btn-sm btn-white"
                draggable
                onDragStart={(event) => handleNodeDragStart(event, nodeType.type)}
                onClick={() => onSelectAddNodeType(nodeType.type)}
                title={`${nodeType.label}: ${nodeType.description} Drag onto the canvas or connect from a node handle to add.`}
                aria-label={nodeType.label}
                aria-pressed={isSelected}
                style={{
                  borderColor: isSelected ? nodeType.color : undefined,
                  backgroundColor: isSelected ? `${nodeType.color}1a` : undefined,
                  color: nodeType.color,
                }}
              >
                <i className={`bi ${nodeType.icon}`} />
              </button>
            );
          })}
        </div>
        {typeof onAutoLayout === 'function' && (
          <button
            type="button"
            className="btn btn-sm btn-white ms-1"
            onClick={() => onAutoLayout()}
            disabled={autoLayoutDisabled}
            title="Auto layout — reposition all steps using the built-in algorithm"
            aria-label="Auto layout"
          >
            <i className="bi bi-diagram-3" />
          </button>
        )}
        {/* A button rather than a switch: it belongs to the row of canvas controls either side
            of it, and a lone switch in a toolbar of icon buttons reads as a setting rather than
            a view you can flip back. `aria-pressed` is what carries the on/off state, so the
            affordance changes without the state becoming invisible. */}
        <button
          type="button"
          className={`btn btn-sm ms-2 ${showDetailedContent ? 'btn-secondary' : 'btn-white'}`}
          onClick={() => onToggleDetailedContent?.(!showDetailedContent)}
          aria-pressed={showDetailedContent}
          title={
            showDetailedContent
              ? 'Detailed nodes: on — each step shows its body text, media and actions. Click to show titles only.'
              : 'Detailed nodes: off — each step shows its title only. Click to show body text, media and actions.'
          }
        >
          <i className="bi bi-card-text me-2" />
          Detailed nodes
        </button>
      </div>

      <div className="d-flex align-items-center">
        {warningItems.length > 0 && (
          hasClickableIssues ? (
            <div className="dropdown mx-2">
              <button
                type="button"
                className="btn btn-sm btn-white dropdown-toggle d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label={`${warningItems.length} publish validation issue(s)`}
                title="Click an issue to locate it on the canvas"
              >
                <i className="bi bi-exclamation-triangle-fill text-warning me-1" />
                <span className="small">{warningItems.length}</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ maxWidth: '28rem' }}>
                {warningItems.map((item, index) => (
                  <li key={`${index}-${item.message}`}>
                    <button
                      type="button"
                      className="dropdown-item small text-wrap"
                      onClick={() => onValidationIssueClick?.(item)}
                    >
                      <i
                        className={`bi ${
                          (item.severity || 'error') === 'warning'
                            ? 'bi-exclamation-circle text-warning'
                            : 'bi-x-circle text-danger'
                        } me-2`}
                      />
                      {item.message}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <span
              className="text-muted d-inline-flex align-items-center mx-2"
              title={warningsTooltip}
              aria-label={`${warningItems.length} publish validation warning(s)`}
              style={{ cursor: 'help' }}
            >
              <i className="bi bi-exclamation-triangle-fill" />
            </span>
          )
        )}
        <div className="text-tiny mb-2 mx-2">Actions</div>
        {saveStatus && (
          <span className="text-success me-3 small">
            {saveStatus}
          </span>
        )}

        <button
          className="btn btn-sm btn-primary me-2"
          onClick={() => onPreview(workflowName)}
        >
          <i className="bi bi-eye me-2"></i>
          Preview
        </button>
        {workflowState === 'draft' && onPublish && (
          <button
            type="button"
            className="btn btn-sm btn-success me-2"
            onClick={onPublish}
            disabled={publishDisabled}
            title={publishDisabled ? 'Fix validation errors before publishing' : undefined}
          >
            <i className="bi bi-cloud-upload me-2" />
            Publish
          </button>
        )}
        {/* The other exit from a draft, next to the one that was previously the only exit.
            Publishing is not the only thing an admin may want to do with a draft they regret,
            and the editor offers the fork that creates one — so it has to offer the way out.
            Worded for which draft this is: a draft that replaces a published version only
            drops pending changes, while an orphan draft is the whole workflow. */}
        {workflowState === 'draft' && onDiscardDraft && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger me-2"
            onClick={onDiscardDraft}
            title={
              replaces
                ? `Discard this draft — workflow #${replaces.id} stays live`
                : 'Delete this workflow — it has never been published, so nothing is left behind'
            }
          >
            <i className="bi bi-trash me-2" />
            {replaces ? 'Discard draft' : 'Delete workflow'}
          </button>
        )}
      </div>
    </div>
  );
}
